import { useAtomValue } from "@effect/atom-react";
import { Atom } from "effect/unstable/reactivity";

import { appAtomRegistry } from "./atomRegistry";

export const SLOW_RPC_ACK_THRESHOLD_MS = 15_000;
export const MAX_TRACKED_RPC_ACK_REQUESTS = 256;
let slowRpcAckThresholdMs = SLOW_RPC_ACK_THRESHOLD_MS;

export interface SlowRpcAckRequest {
  readonly clientId: string;
  readonly requestId: string;
  readonly startedAt: string;
  readonly startedAtMs: number;
  readonly tag: string;
  readonly thresholdMs: number;
}

interface PendingRpcAckRequest {
  readonly request: SlowRpcAckRequest;
  readonly timeoutId: ReturnType<typeof setTimeout>;
}

const pendingRpcAckRequests = new Map<string, PendingRpcAckRequest>();

const slowRpcAckRequestsAtom = Atom.make<ReadonlyArray<SlowRpcAckRequest>>([]).pipe(
  Atom.keepAlive,
  Atom.withLabel("slow-rpc-ack-requests"),
);

function setSlowRpcAckRequests(requests: ReadonlyArray<SlowRpcAckRequest>) {
  appAtomRegistry.set(slowRpcAckRequestsAtom, [...requests]);
}

function getSlowRpcAckRequestsValue(): ReadonlyArray<SlowRpcAckRequest> {
  return appAtomRegistry.get(slowRpcAckRequestsAtom);
}

const DEFAULT_RPC_CLIENT_ID = "global";

const INTERNAL_RPC_METHODS = new Set(["server.heartbeat"]);

function shouldTrackRpcAck(tag: string): boolean {
  const method = tag.split(".").at(-1) ?? tag;
  return !method.startsWith("subscribe") && !INTERNAL_RPC_METHODS.has(tag);
}

export function getSlowRpcAckRequests(): ReadonlyArray<SlowRpcAckRequest> {
  return getSlowRpcAckRequestsValue();
}

export function trackRpcRequestSent(
  requestId: string,
  tag: string,
  clientId = DEFAULT_RPC_CLIENT_ID,
): void {
  if (!shouldTrackRpcAck(tag)) {
    return;
  }

  clearTrackedRpcRequest(requestId, clientId);
  evictOldestPendingRpcRequestIfNeeded();

  const startedAtMs = Date.now();
  const request: SlowRpcAckRequest = {
    clientId,
    requestId,
    startedAt: new Date(startedAtMs).toISOString(),
    startedAtMs,
    tag,
    thresholdMs: slowRpcAckThresholdMs,
  };
  const requestKey = rpcRequestKey(clientId, requestId);
  const timeoutId = setTimeout(() => {
    pendingRpcAckRequests.delete(requestKey);
    appendSlowRpcAckRequest(request);
  }, slowRpcAckThresholdMs);

  pendingRpcAckRequests.set(requestKey, {
    request,
    timeoutId,
  });
}

export function acknowledgeRpcRequest(requestId: string, clientId = DEFAULT_RPC_CLIENT_ID): void {
  clearTrackedRpcRequest(requestId, clientId);
  const slowRequests = getSlowRpcAckRequestsValue();
  if (
    !slowRequests.some(
      (request) => request.clientId === clientId && request.requestId === requestId,
    )
  ) {
    return;
  }

  setSlowRpcAckRequests(
    slowRequests.filter(
      (request) => request.clientId !== clientId || request.requestId !== requestId,
    ),
  );
}

export function clearTrackedRpcRequests(clientId: string): void {
  for (const [requestKey, pending] of pendingRpcAckRequests) {
    if (pending.request.clientId !== clientId) continue;
    clearTimeout(pending.timeoutId);
    pendingRpcAckRequests.delete(requestKey);
  }
  setSlowRpcAckRequests(
    getSlowRpcAckRequestsValue().filter((request) => request.clientId !== clientId),
  );
}

export function clearAllTrackedRpcRequests(): void {
  for (const pending of pendingRpcAckRequests.values()) {
    clearTimeout(pending.timeoutId);
  }
  pendingRpcAckRequests.clear();
  setSlowRpcAckRequests([]);
}

function rpcRequestKey(clientId: string, requestId: string): string {
  return `${clientId}:${requestId}`;
}

function clearTrackedRpcRequest(requestId: string, clientId: string): void {
  const requestKey = rpcRequestKey(clientId, requestId);
  const pending = pendingRpcAckRequests.get(requestKey);
  if (!pending) {
    return;
  }

  clearTimeout(pending.timeoutId);
  pendingRpcAckRequests.delete(requestKey);
}

function appendSlowRpcAckRequest(request: SlowRpcAckRequest): void {
  const requests = [...getSlowRpcAckRequestsValue(), request];
  if (requests.length <= MAX_TRACKED_RPC_ACK_REQUESTS) {
    setSlowRpcAckRequests(requests);
    return;
  }

  setSlowRpcAckRequests(requests.slice(-MAX_TRACKED_RPC_ACK_REQUESTS));
}

function evictOldestPendingRpcRequestIfNeeded(): void {
  while (pendingRpcAckRequests.size >= MAX_TRACKED_RPC_ACK_REQUESTS) {
    const oldestRequestKey = pendingRpcAckRequests.keys().next().value;
    if (oldestRequestKey === undefined) {
      return;
    }

    const pending = pendingRpcAckRequests.get(oldestRequestKey);
    if (!pending) return;
    clearTrackedRpcRequest(pending.request.requestId, pending.request.clientId);
  }
}

export function resetRequestLatencyStateForTests(): void {
  slowRpcAckThresholdMs = SLOW_RPC_ACK_THRESHOLD_MS;
  clearAllTrackedRpcRequests();
}

export function setSlowRpcAckThresholdMsForTests(thresholdMs: number): void {
  slowRpcAckThresholdMs = thresholdMs;
}

export function useSlowRpcAckRequests(): ReadonlyArray<SlowRpcAckRequest> {
  return useAtomValue(slowRpcAckRequestsAtom);
}
